import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Building2, Users, HardHat, ChevronRight, ChevronLeft, Link2, CheckSquare, Square, FileText } from "lucide-react";
import StatusBadge from "./StatusBadge";
import { fmtDate } from "../lib/utils-app";

const inp = "mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg";

export default function ShareBuilderDialog({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [workerIds, setWorkerIds] = useState(new Set());
  const [siteId, setSiteId] = useState("");
  const [pkg, setPkg] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [form, setForm] = useState({ nome: "", expires_at: "", password: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/companies").then(r => setCompanies(r.data)); }, []);
  useEffect(() => {
    setWorkerIds(new Set()); setSiteId(""); setPkg(null);
    if (!companyId) { setWorkers([]); setSites([]); return; }
    Promise.all([api.get("/workers", { params: { company_id: companyId } }), api.get("/sites", { params: { company_id: companyId } })])
      .then(([w, s]) => { setWorkers(w.data.filter(x => x.stato !== "cessato")); setSites(s.data); });
  }, [companyId]);

  const loadPackage = async () => {
    if (!companyId) { toast.error("Seleziona l'azienda"); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/share-package`, { params: { worker_ids: [...workerIds].join(","), site_id: siteId } });
      setPkg(data); setExcluded(new Set()); setStep(2);
      const c = companies.find(x => x.id === companyId);
      if (!form.nome) setForm(f => ({ ...f, nome: `Documentazione ${c?.ragione_sociale || ""}${data.site ? ` – ${data.site.denominazione}` : ""}` }));
    } catch { toast.error("Errore nel comporre il pacchetto"); }
    finally { setBusy(false); }
  };

  const allDocs = useMemo(() => pkg ? [...pkg.company_documents, ...pkg.workers.flatMap(w => w.documents), ...(pkg.site?.documents || [])] : [], [pkg]);
  const includedIds = allDocs.filter(d => !excluded.has(d.id)).map(d => d.id);
  const toggleDoc = (id) => { const n = new Set(excluded); n.has(id) ? n.delete(id) : n.add(id); setExcluded(n); };
  const toggleGroup = (docs) => {
    const n = new Set(excluded);
    const allIn = docs.every(d => !n.has(d.id));
    docs.forEach(d => allIn ? n.add(d.id) : n.delete(d.id));
    setExcluded(n);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!includedIds.length) { toast.error("Nessun documento incluso"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shares", { ...form, document_ids: includedIds, company_id: companyId, worker_ids: [...workerIds], site_id: siteId });
      const url = `${window.location.origin}/s/${data.token}`;
      navigator.clipboard?.writeText(url);
      toast.success("Link creato e copiato negli appunti");
      onCreated(); onClose();
    } catch { toast.error("Errore creazione link"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="share-builder">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Link2 size={18} /> Nuova condivisione documenti</h3>
            <Steps step={step} />
          </div>
          <button onClick={onClose} data-testid="share-close"><X size={20} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {step === 1 && (
            <SelectionStep companies={companies} companyId={companyId} setCompanyId={setCompanyId}
              workers={workers} workerIds={workerIds} setWorkerIds={setWorkerIds} sites={sites} siteId={siteId} setSiteId={setSiteId} />
          )}
          {step === 2 && pkg && (
            <SummaryStep pkg={pkg} excluded={excluded} toggleDoc={toggleDoc} toggleGroup={toggleGroup} includedCount={includedIds.length} total={allDocs.length} />
          )}
          {step === 3 && (
            <form id="share-form" onSubmit={submit} className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700" data-testid="share-final-count">
                <b>{includedIds.length}</b> documenti verranno condivisi ({pkg.company_documents.filter(d => !excluded.has(d.id)).length} azienda · {pkg.workers.flatMap(w => w.documents).filter(d => !excluded.has(d.id)).length} lavoratori · {(pkg.site?.documents || []).filter(d => !excluded.has(d.id)).length} cantiere)
              </div>
              <div><label className="text-sm font-medium">Nome del link *</label>
                <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="share-nome" className={inp} /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-sm font-medium">Data scadenza link</label>
                  <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={inp} /></div>
                <div><label className="text-sm font-medium">Password (opzionale)</label>
                  <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="share-password" className={inp} /></div>
              </div>
              <div><label className="text-sm font-medium">Note per il destinatario</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className={inp} /></div>
            </form>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-between gap-2">
          <button type="button" onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="px-4 py-2 text-slate-600 inline-flex items-center gap-1">
            <ChevronLeft size={16} /> {step > 1 ? "Indietro" : "Annulla"}
          </button>
          {step === 1 && <button onClick={loadPackage} disabled={busy || !companyId} data-testid="share-next-1" className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-60 inline-flex items-center gap-1">{busy ? "Composizione…" : "Componi pacchetto"} <ChevronRight size={16} /></button>}
          {step === 2 && <button onClick={() => setStep(3)} disabled={!includedIds.length} data-testid="share-next-2" className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-60 inline-flex items-center gap-1">Continua ({includedIds.length}) <ChevronRight size={16} /></button>}
          {step === 3 && <button type="submit" form="share-form" disabled={busy} data-testid="share-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-60">{busy ? "Creazione…" : "Crea link di condivisione"}</button>}
        </div>
      </div>
    </div>
  );
}

function Steps({ step }) {
  const labels = ["Selezione", "Riepilogo", "Link"];
  return (
    <div className="flex items-center gap-2 mt-1 text-xs">
      {labels.map((l, i) => (
        <React.Fragment key={l}>
          <span className={`px-2 py-0.5 rounded-full ${step === i + 1 ? "bg-slate-900 text-white" : step > i + 1 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{i + 1}. {l}</span>
          {i < 2 && <span className="text-slate-300">›</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function SelectionStep({ companies, companyId, setCompanyId, workers, workerIds, setWorkerIds, sites, siteId, setSiteId }) {
  const toggleW = (id) => { const n = new Set(workerIds); n.has(id) ? n.delete(id) : n.add(id); setWorkerIds(n); };
  const allW = workers.length > 0 && workers.every(w => workerIds.has(w.id));
  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-medium flex items-center gap-2"><Building2 size={16} /> Azienda *</label>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="share-company" className={inp}>
          <option value="">— Seleziona azienda —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
        </select>
        <p className="text-xs text-slate-500 mt-1">I documenti aziendali già archiviati verranno richiamati automaticamente.</p>
      </div>
      {companyId && (
        <>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2"><Users size={16} /> Lavoratori ({workerIds.size} selezionati)</label>
              {workers.length > 0 && <button type="button" onClick={() => setWorkerIds(allW ? new Set() : new Set(workers.map(w => w.id)))} data-testid="share-workers-all" className="text-xs text-blue-700 hover:underline">{allW ? "Deseleziona tutti" : "Seleziona tutti"}</button>}
            </div>
            <div className="mt-2 border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-100">
              {workers.length === 0 && <div className="p-3 text-sm text-slate-500">Nessun lavoratore attivo.</div>}
              {workers.map(w => (
                <label key={w.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={workerIds.has(w.id)} onChange={() => toggleW(w.id)} data-testid={`share-worker-${w.id}`} />
                  <span className="flex-1">{w.cognome} {w.nome}</span><span className="text-xs text-slate-500">{w.mansione}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium flex items-center gap-2"><HardHat size={16} /> Cantiere (opzionale)</label>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="share-site" className={inp}>
              <option value="">— Nessun cantiere —</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.denominazione}{s.indirizzo ? ` – ${s.indirizzo}` : ""}</option>)}
            </select>
          </div>
        </>
      )}
    </div>
  );
}

function Group({ title, icon: Icon, docs, excluded, toggleDoc, toggleGroup, testid, subtitle }) {
  const included = docs.filter(d => !excluded.has(d.id)).length;
  const allIn = docs.length > 0 && included === docs.length;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Icon size={16} /> {title} <span className="text-slate-500 font-normal">{included}/{docs.length}</span>{subtitle && <span className="text-xs text-slate-500 font-normal">· {subtitle}</span>}</div>
        {docs.length > 0 && <button type="button" onClick={() => toggleGroup(docs)} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">{allIn ? <><Square size={12} /> Escludi tutti</> : <><CheckSquare size={12} /> Includi tutti</>}</button>}
      </div>
      {docs.length === 0 ? <div className="px-4 py-3 text-sm text-slate-400">Nessun documento archiviato.</div> : (
        <div className="divide-y divide-slate-100">
          {docs.map(d => (
            <label key={d.id} className={`flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-slate-50 ${excluded.has(d.id) ? "opacity-50" : ""}`}>
              <input type="checkbox" checked={!excluded.has(d.id)} onChange={() => toggleDoc(d.id)} data-testid={`share-doc-${d.id}`} />
              <FileText size={14} className="text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0"><div className="truncate font-medium">{d.nome}</div><div className="text-xs text-slate-500">{d.categoria}{d.data_scadenza && ` · scad. ${fmtDate(d.data_scadenza)}`}</div></div>
              <StatusBadge status={d.status} />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryStep({ pkg, excluded, toggleDoc, toggleGroup, includedCount, total }) {
  return (
    <div className="space-y-4" data-testid="share-summary">
      <div className="text-sm text-slate-600">Riepilogo del pacchetto documentale: <b>{includedCount}</b> di {total} documenti inclusi. Deseleziona i file da escludere.</div>
      <Group title="Documenti Azienda" icon={Building2} docs={pkg.company_documents} excluded={excluded} toggleDoc={toggleDoc} toggleGroup={toggleGroup} testid="share-group-company" subtitle={pkg.company.ragione_sociale} />
      {pkg.workers.map(w => (
        <Group key={w.worker_id} title={`Documenti Lavoratore – ${w.worker}`} icon={Users} docs={w.documents} excluded={excluded} toggleDoc={toggleDoc} toggleGroup={toggleGroup} testid={`share-group-worker-${w.worker_id}`} subtitle={w.mansione} />
      ))}
      {pkg.site && <Group title={`Documenti Cantiere – ${pkg.site.denominazione}`} icon={HardHat} docs={pkg.site.documents} excluded={excluded} toggleDoc={toggleDoc} toggleGroup={toggleGroup} testid="share-group-site" subtitle={pkg.site.indirizzo} />}
    </div>
  );
}
